import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Suspense, lazy } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPublicSongDownloadUrl, getPublicSongMediaDownloadUrl, requestInstrumental, getInstrumentalStatus, waitForInstrumental, requestWav, getWavStatus, waitForWav, updateAudioChartsVisibility, } from "@/shared/api";
import { Button, Menu, ActionSheet, notifications, InstrumentalConfirmModal, SegmentedControl, Switch, } from "@/shared/ui";
import { IconDownload, IconMovie, IconMicrophone2, IconMusic, IconSend, IconWand, IconChevronLeft, IconPlayerPlay, IconDots, IconX, } from "@tabler/icons-react";
import { useMediaQuery } from "@/shared/lib";
import { buildTrackActions, useTrackActions } from "@/features/track-actions";
import { isActionAvailable } from "@/shared/lib/trackActionAvailability";
import { trackActionErrorMessage } from "@/shared/lib/trackActionErrors";
import { openExternalUrl } from "@/shared/lib/miniAppOpenLink";
import { useAuth } from "@/app/providers/AuthProvider";
import { useLanguage } from "@/app/providers/LanguageProvider";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { BarePlayer } from "./BarePlayer";
import { HubVideoPlayer } from "./HubVideoPlayer";
import { localizeLyrics } from "@/shared/lib/lyricsSections";
import styles from "./SongPublicPage.module.scss";
// Конструктор «Поделиться» тяжёлый (framer-motion/dompurify) и нужен только
// владельцу по клику — грузим ленивым чанком (как в MySongsPage).
const ShareEditorModal = lazy(() => import("@/features/share/ui/ShareEditorModal").then((m) => ({
    default: m.ShareEditorModal,
})));
const TAB_KEYS = ["song", "video", "karaoke"];
/** URL медиа с фрагментом первого кадра (`#t=…`) — превью-кадр из видео. */
function videoFrameSrc(url) {
    if (!url)
        return undefined;
    return url.includes("#") ? url : `${url}#t=0.1`;
}
/** Клик по невидимому якорю `<a download>` — низкоуровневый триггер (веб). */
function triggerHrefDownload(href) {
    const link = document.createElement("a");
    link.href = href;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
/**
 * Переиспользуемое ТЕЛО хаба песни (левый блок: обложка/название/действия +
 * правый блок: табы Песня/Видеообложка/Караоке). Работает поверх `PublicSong`
 * (тот же payload, что отдаёт by-token ручка) и живёт в двух местах:
 *  - `SongPublicPage` (variant="page") — standalone/embedded страница песни;
 *  - `SongDetailPanel` (variant="panel") — панель деталей в «Моей музыке».
 * Вся бизнес-логика (создание минуса/WAV, чарт, поделиться, табы, скачивание)
 * общая — расходится только раскладка и способ воспроизведения аудио.
 */
export const SongHub = ({ token, data, variant = "page", onClose }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const isPanel = variant === "panel";
    const isMobile = useMediaQuery("(max-width: 770px)");
    const [shareOpen, setShareOpen] = React.useState(false);
    // Дополнительные действия (bottom-sheet на мобилке) — как в «Моей музыке».
    const [moreSheetOpen, setMoreSheetOpen] = React.useState(false);
    // Активный таб. Инициализируем из ?tab (только для страницы; в панели deep-link
    // не используется). ?media доуточняет в эффекте ниже, когда данные загружены.
    const [activeTab, setActiveTab] = React.useState(() => {
        if (isPanel)
            return "song";
        const t = searchParams.get("tab");
        return t === "video" || t === "karaoke" ? t : "song";
    });
    // Одиночный просмотр версии видео/караоке (media_output_id) либо null (сетка).
    const [selectedVideoId, setSelectedVideoId] = React.useState(null);
    const [selectedKaraokeId, setSelectedKaraokeId] = React.useState(null);
    // Минус: локальный url (после создания владельцем) поверх пришедшего в data.
    const [createdInstrUrl, setCreatedInstrUrl] = React.useState(null);
    const [instrBusy, setInstrBusy] = React.useState(false);
    const [confirmInstrOpen, setConfirmInstrOpen] = React.useState(false);
    // WAV: локальный url (после заказа владельцем) поверх пришедшего в data.wav.
    const [createdWavUrl, setCreatedWavUrl] = React.useState(null);
    const [wavBusy, setWavBusy] = React.useState(false);
    // Видимость в чартах (оптимистичный локальный стейт).
    const [inCharts, setInCharts] = React.useState(false);
    const [chartsBusy, setChartsBusy] = React.useState(false);
    // Источник плеера/скачивания в левой колонке: полный трек или минус.
    const [audioSource, setAudioSource] = React.useState("song");
    // Ключ кнопки, чьё скачивание сейчас идёт (для спиннера) — за раз одна операция.
    const [downloadingKey, setDownloadingKey] = React.useState(null);
    const { isAuthenticated, user } = useAuth();
    const { language } = useLanguage();
    // Из глобального плеера нужен только `pause` — в панели старт видео/караоке
    // должен заглушить нижний бар (единый источник звука). На странице (variant
    // page) свой BarePlayer, глобальный бар глушим на входе.
    const { pause } = usePlayer();
    const t = React.useCallback((ru, en) => (language === "ru" ? ru : en), [language]);
    // Панель контента таба — для авто-скролла по deep-link ?media (только page).
    const panelRef = React.useRef(null);
    // Хэндл мини-плеера — чтобы поставить аудио на паузу при старте видео (page).
    const barePlayerRef = React.useRef(null);
    const isOwner = isAuthenticated &&
        data.owner_user_id != null &&
        user?.id === data.owner_user_id;
    // Разрешено ли действие контрактом бэка (`provider` + `available_actions` в
    // ответе песни). Старый бэк этих полей не отдаёт — тогда ничего не запрещено
    // и набор кнопок собирается прежними локальными условиями (владелец и т.п.).
    const can = (action) => isActionAvailable(data, action);
    // Вторичные действия (ссылки на подарок · повторить генерацию · ИИ-голос) —
    // тот же контроллер, что в плеере/списке. Избранное/скрыть/удалить в публичном
    // payload недоступны (нет состояния, контракт бэка не трогаем), поэтому «Ещё»
    // здесь охватывает действия, которые payload хаба поддерживает.
    const secondary = useTrackActions({
        language,
        audioId: data.audio_id,
        songId: data.song_id,
        title: data.title,
        isShowInCharts: inCharts,
        onPatch: (patch) => {
            if (patch.isShowInCharts != null)
                setInCharts(patch.isShowInCharts);
        },
    });
    // Скачивание полного трека по ПРЯМОЙ ссылке (presigned) из хранилища. Без
    // токена (панель) качаем сразу по data.audio_url; с токеном — через by-token
    // download-ручку (короткоживущая ссылка) с фолбэком на прямой url.
    const handleDownload = React.useCallback(async () => {
        setDownloadingKey("song");
        try {
            let href = data?.audio_url ?? "";
            if (token) {
                try {
                    href = await getPublicSongDownloadUrl(token);
                }
                catch (e) {
                    console.error("SongHub: download-url failed, fallback to audio_url", e);
                }
            }
            if (!href)
                return;
            if (openExternalUrl(href))
                return;
            triggerHrefDownload(href);
        }
        finally {
            setDownloadingKey(null);
        }
    }, [token, data]);
    // Скачивание МЕДИА (минус/WAV/видео/караоке) по by-token download-url.
    const downloadMedia = React.useCallback(async (kind, mediaOutputId, fallbackUrl) => {
        setDownloadingKey(mediaOutputId != null ? `${kind}-${mediaOutputId}` : kind);
        try {
            let href = fallbackUrl ?? "";
            if (token) {
                try {
                    href = await getPublicSongMediaDownloadUrl(token, kind, mediaOutputId ?? undefined);
                }
                catch (e) {
                    console.error("SongHub: media download-url failed, fallback to direct url", e);
                }
            }
            if (!href)
                return;
            if (openExternalUrl(href))
                return;
            triggerHrefDownload(href);
        }
        finally {
            setDownloadingKey(null);
        }
    }, [token]);
    // Авто-старт скачивания полного трека при открытии (ссылки от ботов) — ТОЛЬКО
    // на странице (variant page), для простой песни без доп. медиа и НЕ у владельца.
    const autoStartedRef = React.useRef(false);
    React.useEffect(() => {
        if (isPanel || !data || autoStartedRef.current)
            return;
        const owner = isAuthenticated &&
            data.owner_user_id != null &&
            user?.id === data.owner_user_id;
        const hasExtraMedia = Boolean((data.media_outputs ?? []).some((m) => m.status === "ready") ||
            data.instrumental?.url ||
            data.wav?.url);
        if (owner || hasExtraMedia)
            return;
        autoStartedRef.current = true;
        void handleDownload();
    }, [isPanel, data, handleDownload, isAuthenticated, user]);
    // Синхронизация активного таба/медиа с query (?tab / ?media) — только страница.
    const lastQueryKeyRef = React.useRef(null);
    React.useEffect(() => {
        if (isPanel || !data)
            return;
        const tabParam = searchParams.get("tab");
        const mediaParam = searchParams.get("media");
        const key = `${tabParam ?? ""}|${mediaParam ?? ""}`;
        if (key === lastQueryKeyRef.current)
            return;
        lastQueryKeyRef.current = key;
        let nextTab = tabParam === "video" || tabParam === "karaoke" || tabParam === "song"
            ? tabParam
            : null;
        const mediaId = mediaParam != null ? Number(mediaParam) : NaN;
        if (Number.isFinite(mediaId)) {
            const mo = (data.media_outputs ?? []).find((m) => m.id === mediaId);
            if (mo && mo.kind === "video_cover") {
                nextTab = "video";
                setSelectedVideoId(mo.id);
            }
            else if (mo && mo.kind === "karaoke") {
                nextTab = "karaoke";
                setSelectedKaraokeId(mo.id);
            }
        }
        if (nextTab)
            setActiveTab(nextTab);
        if (mediaParam != null) {
            window.requestAnimationFrame(() => {
                panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }, [isPanel, data, searchParams]);
    // Deep-link ?action=extend|instrumental|videocover|karaoke — авто-триггер
    // действия после загрузки данных (кнопки из ботов, открывающие миниап сразу на
    // действии). Только страница (variant page) и только владелец при доступном
    // аудио; иначе параметр игнорируется без ошибок. Срабатывает один раз.
    const autoActionRef = React.useRef(false);
    React.useEffect(() => {
        if (isPanel || !data || autoActionRef.current)
            return;
        const action = searchParams.get("action");
        if (!action)
            return;
        // owner-only.
        const owner = isAuthenticated &&
            data.owner_user_id != null &&
            user?.id === data.owner_user_id;
        if (!owner)
            return;
        // Действие, недоступное этому треку, не запускаем (параметр всё равно
        // снимаем ниже) — иначе кнопка бота привела бы к отказу бэка.
        let handled = true;
        switch (action) {
            case "extend":
                if (can("extend") || can("replace_section"))
                    goExtend();
                break;
            case "videocover":
                if (can("video_cover")) {
                    setActiveTab("video");
                    goVideoCover();
                }
                break;
            case "karaoke":
                if (can("karaoke")) {
                    setActiveTab("karaoke");
                    goKaraoke();
                }
                break;
            case "instrumental":
                if (can("make_instrumental"))
                    setConfirmInstrOpen(true);
                break;
            default:
                handled = false;
        }
        if (!handled)
            return;
        autoActionRef.current = true;
        // Убираем action из query, сохранив прочие параметры, чтобы не сработало повторно.
        const next = new URLSearchParams(searchParams);
        next.delete("action");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPanel, data, searchParams, isAuthenticated, user]);
    // На СТРАНИЦЕ песни глушим глобальный плеер на входе (нижний бар «Моих песен»
    // мог играть) — там своя аудио-дорожка (BarePlayer). В ПАНЕЛИ, наоборот, звук
    // идёт через глобальный плеер, поэтому его НЕ трогаем.
    React.useEffect(() => {
        if (!isPanel)
            pause();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Начальное состояние тумблера «В чарт» из payload (когда бэк его отдаёт).
    React.useEffect(() => {
        if (data?.is_show_in_charts != null)
            setInCharts(data.is_show_in_charts);
    }, [data]);
    const selectTab = (key) => {
        setActiveTab(key);
        if (isPanel)
            return; // в панели URL не трогаем
        const next = new URLSearchParams(searchParams);
        next.set("tab", key);
        next.delete("media");
        setSearchParams(next, { replace: true });
    };
    const lyrics = data.text?.trim();
    const lyricSegments = lyrics ? localizeLyrics(lyrics, language) : [];
    // ── Медиа-выходы хаба ─────────────────────────────────────────────────────
    const mediaOutputs = data.media_outputs ?? [];
    const sortByCreated = (a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id;
    const videoOutputs = mediaOutputs
        .filter((m) => m.kind === "video_cover" && m.status === "ready")
        .sort(sortByCreated);
    const karaokeOutputs = mediaOutputs
        .filter((m) => m.kind === "karaoke" && m.status === "ready")
        .sort(sortByCreated);
    const instrumentalUrl = createdInstrUrl ?? data.instrumental?.url ?? null;
    const wavUrl = createdWavUrl ?? data.wav?.url ?? null;
    // Что играет мини-плеер (page): минус (если выбран и готов) или полный трек.
    const playerSrc = audioSource === "minus" && instrumentalUrl ? instrumentalUrl : data.audio_url;
    // Старт видео/караоке глушит аудио: на странице — локальный BarePlayer, в
    // панели — глобальный нижний плеер (единый источник звука).
    const handleVideoPlay = React.useCallback(() => {
        if (isPanel)
            pause();
        else
            barePlayerRef.current?.pause();
    }, [isPanel, pause]);
    // ── Создание минуса (owner-only, платно) ──────────────────────────────────
    const applyInstrReady = async (knownUrl) => {
        let url = knownUrl ?? null;
        if (!url && data.audio_id != null) {
            try {
                const st = await getInstrumentalStatus(data.audio_id);
                url = st.url ?? null;
            }
            catch {
                /* игнорируем — минус готов и без прямого url (качаем через ручку) */
            }
        }
        setCreatedInstrUrl(url);
        notifications.show({
            title: t("Минус готов", "Instrumental ready"),
            message: t("Можно послушать и скачать ниже.", "You can listen and download it below."),
            color: "green",
        });
    };
    const startInstrumental = async () => {
        const audioId = data.audio_id;
        if (audioId == null || instrBusy)
            return;
        setInstrBusy(true);
        let started;
        try {
            started = await requestInstrumental(audioId);
        }
        catch (e) {
            const httpStatus = e?.response
                ?.status;
            if (httpStatus !== 402) {
                notifications.show({
                    color: "red",
                    title: t("Ошибка", "Error"),
                    message: trackActionErrorMessage(e, language === "ru") ??
                        t("Не удалось заказать минус.", "Failed to request instrumental."),
                });
            }
            setInstrBusy(false);
            return;
        }
        if (started.status === "ready" && started.url) {
            await applyInstrReady(started.url);
            setInstrBusy(false);
            return;
        }
        if (!started.ws_event_id) {
            notifications.show({
                title: t("Готовим минус", "Preparing instrumental"),
                message: t("Загляните чуть позже.", "Check back a bit later."),
            });
            setInstrBusy(false);
            return;
        }
        notifications.show({
            title: t("Готовим минус", "Preparing instrumental"),
            message: t("Это займёт до пары минут.", "This can take a couple of minutes."),
        });
        waitForInstrumental(started.ws_event_id, async () => {
            await applyInstrReady();
            setInstrBusy(false);
        }, async () => {
            try {
                const st = await getInstrumentalStatus(audioId);
                if (st.status === "ready") {
                    await applyInstrReady(st.url);
                }
                else if (st.status === "failed") {
                    notifications.show({
                        color: "red",
                        title: t("Не удалось сделать минус", "Failed to make instrumental"),
                        message: t("Средства возвращены. Попробуйте ещё раз.", "Refunded. Please try again."),
                    });
                }
            }
            catch {
                /* игнорируем — просто снимем индикатор загрузки */
            }
            setInstrBusy(false);
        });
    };
    // ── WAV (owner-only, бесплатно) ───────────────────────────────────────────
    const applyWavReady = async (knownUrl) => {
        let url = knownUrl ?? null;
        if (!url && data.audio_id != null) {
            try {
                const st = await getWavStatus(data.audio_id);
                url = st.url ?? null;
            }
            catch {
                /* игнорируем — WAV готов и без прямого url (качаем через ручку) */
            }
        }
        setCreatedWavUrl(url);
        notifications.show({
            title: t("WAV готов", "WAV ready"),
            message: t("Можно скачать ниже.", "You can download it below."),
            color: "green",
        });
    };
    const startWav = async () => {
        const audioId = data.audio_id;
        if (audioId == null || wavBusy)
            return;
        setWavBusy(true);
        let started;
        try {
            started = await requestWav(audioId);
        }
        catch (e) {
            notifications.show({
                color: "red",
                title: t("Ошибка", "Error"),
                message: trackActionErrorMessage(e, language === "ru") ??
                    t("Не удалось заказать WAV.", "Failed to request WAV."),
            });
            setWavBusy(false);
            return;
        }
        if (started.status === "ready" && started.url) {
            await applyWavReady(started.url);
            setWavBusy(false);
            return;
        }
        if (!started.ws_event_id) {
            notifications.show({
                title: t("Готовим WAV", "Preparing WAV"),
                message: t("Загляните чуть позже.", "Check back a bit later."),
            });
            setWavBusy(false);
            return;
        }
        notifications.show({
            title: t("Готовим WAV", "Preparing WAV"),
            message: t("Это займёт немного времени.", "This can take a moment."),
        });
        waitForWav(started.ws_event_id, async () => {
            await applyWavReady();
            setWavBusy(false);
        }, async () => {
            try {
                const st = await getWavStatus(audioId);
                if (st.status === "ready") {
                    await applyWavReady(st.url);
                }
                else if (st.status === "failed") {
                    notifications.show({
                        color: "red",
                        title: t("Не удалось сделать WAV", "Failed to make WAV"),
                        message: t("Попробуйте ещё раз.", "Please try again."),
                    });
                }
            }
            catch {
                /* игнорируем — просто снимем индикатор загрузки */
            }
            setWavBusy(false);
        });
    };
    // Гейт кнопки WAV: готов → скачиваем; не готов → заказываем (owner-only).
    const handleWav = () => {
        if (wavUrl) {
            void downloadMedia("wav", null, wavUrl);
            return;
        }
        void startWav();
    };
    // ── «В чарт» (owner-only, оптимистично) ───────────────────────────────────
    const handleToggleCharts = async (next) => {
        const audioId = data.audio_id;
        if (audioId == null || chartsBusy)
            return;
        setChartsBusy(true);
        const prev = inCharts;
        setInCharts(next); // оптимистично
        try {
            // Модель стемов-строк (батч 16): чарт-флаг ставится на строку по её
            // audio_id (развилки use_instrumental больше нет). На публичном хабе
            // тумблер управляет полным треком; минус в чартах управляется из «Моей
            // музыки» по строке-минусу.
            await updateAudioChartsVisibility(audioId, {
                in_charts: next,
            });
        }
        catch (e) {
            console.error("SongHub: charts visibility failed", e);
            setInCharts(prev); // откат
            notifications.show({
                color: "red",
                title: t("Ошибка", "Error"),
                message: t("Не удалось обновить чарт.", "Failed to update chart."),
            });
        }
        finally {
            setChartsBusy(false);
        }
    };
    // ── «Поделиться»: владельцу — конструктор, гостю — копия ссылки. ───────────
    const handleShare = async () => {
        if (isOwner && data.audio_id != null) {
            setShareOpen(true);
            return;
        }
        try {
            await navigator.clipboard.writeText(window.location.href);
            notifications.show({
                title: t("Ссылка скопирована", "Link copied"),
                message: "",
                color: "green",
            });
        }
        catch {
            notifications.show({
                title: t("Не удалось скопировать", "Could not copy"),
                message: "",
                color: "red",
            });
        }
    };
    // ── Навигация в разделы создания (маршрут + nav-state) ─────────────────────
    const navState = {
        audioId: data.audio_id,
        title: data.title,
        audioUrl: data.audio_url,
        imageUrl: data.cover_url ?? undefined,
        // Страница действия по ним решает, что доступно треку.
        provider: data.provider,
        actions: data.available_actions,
    };
    const goVideoCover = () => navigate("/video-cover", { state: navState });
    const goKaraoke = () => navigate("/karaoke", { state: navState });
    const goExtend = () => navigate("/edit-track", {
        state: {
            ...navState,
            duration: data.duration ?? undefined,
            songId: data.song_id ?? undefined,
            lyrics: data.text?.trim() || undefined,
        },
    });
    // ── Таб «Песня»: только текст. ────────────────────────────────────────────
    const songTab = (_jsx("div", { className: styles.tabPanelInner, children: _jsxs("section", { className: styles.right, children: [_jsx("h2", { className: styles.lyricsHeading, children: t("Текст песни", "Lyrics") }), lyricSegments.length > 0 ? (_jsx("div", { className: styles.lyrics, children: lyricSegments.map((seg, i) => seg.kind === "heading" ? (_jsx("h3", { className: styles.lyricsSection, children: seg.text }, i)) : (_jsx("p", { className: styles.lyricsBlock, children: seg.text }, i))) })) : (_jsx("p", { className: styles.lyricsEmpty, children: t("Текст недоступен", "No lyrics") }))] }) }));
    // ── Таб медиа (видео/караоке): превью всегда, 1/сетка → одиночный вид ──────
    const renderMediaTab = (outputs, kind, selectedId, setSelectedId, onCreate, labels) => {
        if (outputs.length === 0) {
            return (_jsx("div", { className: styles.tabPanelInner, children: isOwner ? (_jsx(Button, { variant: "primary", size: "md", leftSection: _jsx(IconWand, { size: 18 }), onClick: onCreate, children: labels.create })) : (_jsx("p", { className: styles.sectionEmpty, children: labels.empty })) }));
        }
        const multi = outputs.length > 1;
        const selected = selectedId != null ? outputs.find((o) => o.id === selectedId) : null;
        const single = !multi ? outputs[0] : selected;
        return (_jsxs("div", { className: styles.tabPanelInner, children: [single ? (_jsxs("div", { className: styles.mediaSingle, children: [_jsx(HubVideoPlayer, { src: single.url, label: labels.base, onPlay: handleVideoPlay, onDownload: () => void downloadMedia(kind, single.id, single.url), downloading: downloadingKey === `${kind}-${single.id}` }, single.id), multi && (_jsx("div", { className: styles.mediaActions, children: _jsx(Button, { variant: "ghost", size: "sm", leftSection: _jsx(IconChevronLeft, { size: 16 }), onClick: () => setSelectedId(null), children: t("Все версии", "All versions") }) }))] })) : (_jsx("div", { className: styles.mediaGrid, children: outputs.map((mo, idx) => (_jsxs("button", { type: "button", className: styles.mediaCard, onClick: () => setSelectedId(mo.id), children: [_jsx("video", { className: styles.mediaThumb, src: videoFrameSrc(mo.url), muted: true, playsInline: true, preload: "metadata" }), _jsx("span", { className: styles.mediaPlay, "aria-hidden": "true", children: _jsx("span", { className: styles.mediaPlayBadge, children: _jsx(IconPlayerPlay, { size: 24 }) }) }), _jsxs("span", { className: styles.mediaCardLabel, children: [labels.base, " ", idx + 1] })] }, mo.id))) })), isOwner && (_jsx(Button, { variant: "outline", size: "sm", leftSection: _jsx(IconWand, { size: 16 }), onClick: onCreate, className: styles.mediaCreateMore, children: labels.create }))] }));
    };
    const videoTab = renderMediaTab(videoOutputs, "video-cover", selectedVideoId, setSelectedVideoId, goVideoCover, {
        base: t("Видео", "Video"),
        create: t("Создать видеообложку", "Create video cover"),
        empty: t("Видеообложек пока нет", "No video covers yet"),
    });
    const karaokeTab = renderMediaTab(karaokeOutputs, "karaoke", selectedKaraokeId, setSelectedKaraokeId, goKaraoke, {
        base: t("Караоке", "Karaoke"),
        create: t("Создать караоке", "Create karaoke"),
        empty: t("Караоке пока нет", "No karaoke yet"),
    });
    // Вкладка «Караоке» — только если караоке треку доступно либо клипы уже есть
    // (готовые должны остаться видны). Иначе она была бы пустой с кнопкой
    // «Создать караоке», которая упирается в отказ бэка.
    const tabKeys = TAB_KEYS.filter((key) => key !== "karaoke" || can("karaoke") || karaokeOutputs.length > 0);
    // Вкладка из ссылки (`?tab=karaoke`) могла оказаться скрытой — показываем песню.
    const shownTab = tabKeys.includes(activeTab) ? activeTab : "song";
    const tabLabels = {
        song: { label: t("Песня", "Song"), icon: _jsx(IconMusic, { size: 16 }) },
        video: { label: t("Видеообложка", "Video cover"), icon: _jsx(IconMovie, { size: 16 }) },
        karaoke: { label: t("Караоке", "Karaoke"), icon: _jsx(IconMicrophone2, { size: 16 }) },
    };
    // ── Меню «Ещё»: второстепенные действия, свёрнутые в дропдаун ──────────────
    // Первичные действия (Скачать · Поделиться · В чарт) остаются кнопками/тумблером,
    // остальное собирается ЕДИНОЙ фабрикой (те же лейблы/иконки/порядок, что в
    // списке и плеере) и прячется под «Ещё».
    const moreItems = buildTrackActions({
        language,
        kind: "song",
        // Локальные условия (владелец, готовность) — как раньше; поверх них —
        // контракт бэка `can(...)`: у трека без Suno-действий их нет и здесь.
        actions: [
            ...(isOwner && !instrumentalUrl && can("make_instrumental")
                ? ["make_instrumental"]
                : []),
            // Готовый WAV качается всегда; заказать новый — только если разрешено.
            ...(wavUrl || (isOwner && can("download_wav"))
                ? ["download_wav"]
                : []),
            // Пункт «Изменить или расширить» один на оба режима (ключ extend); какой
            // из них доступен треку, решает экран по `actions` из navState. Без
            // контракта (старый бэк) режим замены экран разрешит сам, а стем бэк
            // отклонит 409.
            ...(isOwner &&
                data.audio_id != null &&
                (can("extend") || can("replace_section"))
                ? ["extend"]
                : []),
            ...(isOwner && data.song_id != null && can("repeat")
                ? ["repeat"]
                : []),
            ...(isOwner && data.audio_id != null && can("share")
                ? ["share"]
                : []),
        ],
        instrReady: false,
        instrBusy,
        wavReady: !!wavUrl,
        wavBusy,
        repeatBusy: secondary.repeatBusy,
        canVoicePersona: isOwner && data.audio_id != null && can("voice_persona"),
        // «Релиз трека» — только владельцу (гость/чужой хаб релизить не может).
        canRelease: isOwner && data.audio_id != null && can("release"),
        onMakeInstrumental: isOwner && !instrumentalUrl ? () => setConfirmInstrOpen(true) : undefined,
        onWav: isOwner
            ? handleWav
            : wavUrl
                ? () => void downloadMedia("wav", null, wavUrl)
                : undefined,
        onEditTrack: isOwner ? goExtend : undefined,
        onRepeat: isOwner ? secondary.onRepeat : undefined,
        onShare: isOwner ? secondary.onShare : undefined,
        onVoicePersona: isOwner ? secondary.onVoicePersona : undefined,
        onRelease: isOwner && data.audio_id != null
            ? () => navigate(`/release?audioId=${data.audio_id}`)
            : undefined,
    });
    const moreControl = moreItems.length > 0 ? (isMobile ? (_jsx(Button, { variant: "outline", size: "md", leftSection: _jsx(IconDots, { size: 16 }), onClick: () => setMoreSheetOpen(true), children: t("Ещё", "More") })) : (_jsx(Menu, { position: "top-start", minWidth: 220, trigger: _jsx(Button, { variant: "outline", size: "md", leftSection: _jsx(IconDots, { size: 16 }), children: t("Ещё", "More") }), items: moreItems }))) : null;
    // ── Левый блок (обложка + название + [источник] + [плеер] + действия) ──────
    const leftBlock = (_jsxs("div", { className: styles.left, children: [data.cover_url ? (_jsx("img", { className: styles.cover, src: data.cover_url, alt: "" })) : (_jsx("div", { className: styles.coverFallback, "aria-hidden": "true", children: _jsx("svg", { width: "64", height: "64", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" }) }) })), isPanel ? (_jsx("div", { className: styles.title, children: data.title || "Песня" })) : (_jsx("h1", { className: styles.title, children: data.title || "Песня" })), instrumentalUrl && (_jsx(SegmentedControl, { fullWidth: true, value: audioSource, onChange: setAudioSource, ariaLabel: t("Источник", "Source"), options: [
                    { value: "song", label: t("Песня", "Song") },
                    { value: "minus", label: t("Минус", "Minus") },
                ] })), !isPanel && (_jsx(BarePlayer, { ref: barePlayerRef, src: playerSrc, autoPlay: true }, playerSrc)), _jsxs("div", { className: styles.leftActions, children: [audioSource === "minus" ? (_jsx(Button, { variant: "primary", size: "lg", onClick: () => void downloadMedia("instrumental", null, instrumentalUrl), loading: downloadingKey === "instrumental", leftSection: _jsx(IconDownload, { size: 18 }), children: t("Скачать минус", "Download instrumental") })) : (_jsx(Button, { variant: "primary", size: "lg", onClick: handleDownload, loading: downloadingKey === "song", leftSection: _jsx(IconDownload, { size: 18 }), children: t("Скачать песню", "Download song") })), isOwner && data.audio_id != null && (_jsxs("label", { className: styles.chartToggle, children: [_jsx("span", { children: t("В чарт", "In chart") }), _jsx(Switch, { checked: inCharts, disabled: chartsBusy, onChange: (checked) => void handleToggleCharts(checked) })] })), _jsxs("button", { type: "button", className: styles.shareBtn, onClick: () => void handleShare(), children: [_jsx(IconSend, { size: 16 }), _jsx("span", { children: t("Поделиться", "Share") })] }), moreControl] })] }));
    // ── Правый блок (таб-бар + контент активного таба) ────────────────────────
    const tabsBlock = (_jsxs("div", { className: styles.tabColumn, ref: panelRef, children: [_jsx("div", { className: styles.tabBar, role: "tablist", children: tabKeys.map((key) => (_jsxs("button", { type: "button", role: "tab", "aria-selected": shownTab === key, className: `${styles.tab} ${shownTab === key ? styles.tabActive : ""}`, onClick: () => selectTab(key), children: [tabLabels[key].icon, _jsx("span", { children: tabLabels[key].label })] }, key))) }), _jsxs("div", { className: styles.tabPanel, role: "tabpanel", children: [shownTab === "song" && songTab, shownTab === "video" && videoTab, shownTab === "karaoke" && karaokeTab] })] }));
    // Модалки (share + подтверждение минуса) — общие для обоих вариантов.
    const modals = (_jsxs(_Fragment, { children: [data.audio_id != null && (_jsx(Suspense, { fallback: null, children: _jsx(ShareEditorModal, { opened: shareOpen, onClose: () => setShareOpen(false), audioId: data.audio_id, audioTitle: data.title, audioUrl: data.audio_url, audioCoverUrl: data.cover_url }) })), _jsx(InstrumentalConfirmModal, { opened: confirmInstrOpen, onClose: () => setConfirmInstrOpen(false), onConfirm: () => startInstrumental(), language: language }), secondary.modals, _jsx(ActionSheet, { open: moreSheetOpen, title: data.title, items: moreItems, onClose: () => setMoreSheetOpen(false) })] }));
    if (isPanel) {
        return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.hubPanel, children: [onClose && (_jsx("button", { type: "button", className: styles.panelClose, onClick: onClose, "aria-label": t("Закрыть", "Close"), children: _jsx(IconX, { size: 18 }) })), leftBlock, tabsBlock] }), modals] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.columns, children: [leftBlock, tabsBlock] }), modals] }));
};
export default SongHub;
